<template>
  <div class="artwork-history-grid-item">
    <div
      class="artwork-history-grid-item__artwork-image-container"
      :class="{
        'artwork-history-grid-item__artwork-image-container--clickable': isShownCheckbox
      }"
      @click="selectLibrary(artwork, index)"
    >
      <img
        class="artwork-history-grid-item__artwork-image"
        :src="artwork.previewUrl"
        :alt="`Artwork - ${artwork.name}`"
      />
      <EnhancementTierLabel
        v-if="isShownTier && artwork.enhancementTier"
        :tier="artwork.enhancementTier"
        class="artwork-history-grid-item__enhancement-tier"
      />
    </div>
    <button
      type="button"
      class="artwork-history-grid-item__download"
      @click="$emit('download-artwork', artwork.downloadUrl, artwork.name)"
    >
      <UseSvg class="artwork-history-grid-item__download-icon" icon="download-line" />
    </button>

    <button
      v-if="isShownCheckbox"
      type="button"
      class="artwork-history-grid-item__checkbox"
      :class="{
        'artwork-history-grid-item__checkbox--selected': isSelected,
        'artwork-history-grid-item__checkbox--standard-artwork': isStandardArtwork
      }"
      @click="selectLibrary(artwork, index)"
    >
      <UseSvg class="artwork-history-grid-item__checkbox-icon" icon="check" />
    </button>
    <div :title="artwork.name" class="artwork-history-grid-item__info-name">
      {{ artwork.name }}
    </div>

    <div class="artwork-history-grid-item__info-text">Size: {{ sizeText(artwork) }}</div>
    <div class="artwork-history-grid-item__info-text artwork-history-grid-item__info-text-date">
      {{ date }}
    </div>
  </div>
</template>
<script>
import { mapState } from "pinia";
import { useI18n } from "vue-i18n";
import UseSvg from "common/vue/components/UseSvg.vue";
import { INCHES_PER_FOOT } from "./common/constants";
import { useDtfStore } from "./store";
import EnhancementTierLabel from "./common/EnhancementTierLabel.vue";

export default {
  name: "ArtworkHistoryGridItem",
  components: { EnhancementTierLabel, UseSvg },
  props: {
    artwork: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isShownTier: {
      type: Boolean,
      default: false
    },
    isShownCheckbox: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    }
  },
  emits: ["select-library", "download-artwork"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  computed: {
    printAsIs() {
      return this.artwork.useFootLength;
    },
    date() {
      if (this.artwork.purchasedDate) return `Purchased on ${this.artwork.purchasedDate}`;
      return `Uploaded on ${this.artwork.uploadedOn}`;
    },
    ...mapState(useDtfStore, ["isStandardArtwork"])
  },
  methods: {
    sizeText({ metadata }) {
      const { widthInch, heightInch } = metadata;
      const height = this.printAsIs ? (heightInch / INCHES_PER_FOOT).toFixed(0) : heightInch;
      const unit = this.printAsIs ? "'" : '"';

      return `${widthInch}" x ${height}${unit}`;
    },
    selectLibrary(artwork, index) {
      if (this.isShownCheckbox) this.$emit("select-library", artwork, index);
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-history-grid-item {
  display: flex;
  flex-direction: column;
  font-family: inherit;
  position: relative;
  width: 160px;
  height: min-content;
  border-radius: 4px;
  margin-bottom: 10px;

  @include with-tablet {
    width: 230px;
  }

  &__checkbox {
    position: absolute;
    width: 32px;
    height: 32px;
    top: 15px;
    left: 15px;
    padding: 5px;
    border-radius: 4px;
    border: 1px solid $gray-border;
    background-color: $white;

    @include with-desktop {
      visibility: hidden;
    }

    &--selected {
      border: 1px solid $borneo-pink-gradient-start;
      background-color: $borneo-pink-gradient-start;
      visibility: visible !important;
    }

    &--selected#{&}--standard-artwork {
      border-color: $primary-light;
      background-color: $primary-light;
    }

    :hover > & {
      visibility: visible;
    }
  }

  &__checkbox-icon {
    width: 20px;
    height: 20px;
    color: $white;
  }

  &__download {
    position: absolute;
    width: 32px;
    height: 32px;
    top: 15px;
    right: 0;
    padding: 5px;
    border-radius: 4px;
    border: 1px solid $gray-border;
    background-color: $white;

    @include with-tablet {
      right: 15px;
    }

    @include with-desktop {
      visibility: hidden;
    }

    :hover > & {
      visibility: visible;
    }
  }

  &__download-icon {
    width: 20px;
    height: 20px;
    color: $app-gray-modern-400;
  }

  &__artwork-image-container {
    width: 160px;
    height: 160px;
    border: 1px solid $gray-border;
    border-radius: 8px;
    overflow: hidden;
    position: relative;

    @include with-large-2-mobile {
      width: 175px;
      height: 175px;
    }

    @include with-tablet {
      width: 227px;
      height: 227px;
    }

    &--clickable {
      cursor: pointer;
    }
  }

  &__artwork-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__info-name {
    font-family: inherit;
    font-size: 18px;
    font-weight: 600;
    text-align: left;
    margin-top: 5px;
    color: $black-light;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: inherit;
  }

  &__info-text {
    font-family: inherit;
    font-size: 16px;
    font-weight: 400;
    color: $app-gray;
  }

  &__info-text-date {
    font-size: 15px !important;
  }

  &__enhancement-tier {
    position: absolute;
    bottom: 15px;
    left: 15px;

    @include with-tablet {
      bottom: 8px;
      left: 8px;
    }
  }
}
</style>
